import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Select, DatePicker, Space, message, Popconfirm, Tag, Card, Spin } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../utils/api';
import { useMenuPermission } from '../hooks/useMenuPermission';
import { useIsMobile } from '../hooks/useIsMobile';

const { RangePicker } = DatePicker;


const TREND_COLORS = ['#1677ff', '#52c41a', '#fa8c16', '#eb2f96', '#722ed1', '#13c2c2', '#f5222d', '#a0d911', '#2f54eb', '#faad14'];

// 轻量 SVG 折线图（无第三方依赖）
function PriceTrendChart({ rows }) {
  const wrapRef = useRef(null);
  const [width, setWidth] = useState(720);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    if (!wrapRef.current) return;
    const el = wrapRef.current;
    const update = () => setWidth(Math.max(320, el.clientWidth));
    update();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (ro) ro.observe(el);
    window.addEventListener('resize', update);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  const height = 300;
  const padL = 56, padR = 24, padT = 20, padB = 34;

  const { series, dates, yMin, yMax } = useMemo(() => {
    const map = new Map();
    (rows || []).forEach(r => {
      const key = `${r.supplier_id}-${r.growth_method_id}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          label: `${r.supplier_name || '-'} / ${r.growth_method_name || '-'}`,
          points: []
        });
      }
      map.get(key).points.push({ date: r.effective_date, value: Number(r.reference_price) });
    });

    const series = Array.from(map.values()).map(s => ({
      ...s,
      points: s.points.sort((a, b) => a.date.localeCompare(b.date))
    }));

    const dateSet = new Set();
    let min = Infinity, max = -Infinity;
    series.forEach(s => s.points.forEach(p => {
      dateSet.add(p.date);
      if (p.value < min) min = p.value;
      if (p.value > max) max = p.value;
    }));
    const dates = Array.from(dateSet).sort();
    if (!isFinite(min)) { min = 0; max = 100; }
    if (min === max) { min = Math.max(0, min - 10); max = max + 10; }
    const pad = (max - min) * 0.12;
    return { series, dates, yMin: Math.max(0, min - pad), yMax: max + pad };
  }, [rows]);

  if (!series.length) {
    return (
      <div style={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', border: '1px dashed #e5e5e5', borderRadius: 8 }}>
        暂无价格调整记录
      </div>
    );
  }

  const innerW = width - padL - padR;
  const innerH = height - padT - padB;

  const xOf = (date) => {
    if (dates.length === 1) return padL + innerW / 2;
    const t0 = new Date(dates[0] + 'T00:00:00').getTime();
    const t1 = new Date(dates[dates.length - 1] + 'T00:00:00').getTime();
    const t = new Date(date + 'T00:00:00').getTime();
    return padL + ((t - t0) / (t1 - t0)) * innerW;
  };
  const yOf = (v) => padT + innerH - ((v - yMin) / (yMax - yMin)) * innerH;

  const yTicks = Array.from({ length: 5 }, (_, i) => yMin + ((yMax - yMin) * i) / 4);
  const xTicks = dates.length <= 8
    ? dates
    : [0, Math.floor(dates.length / 3), Math.floor((dates.length * 2) / 3), dates.length - 1].map(i => dates[i]);

  return (
    <div>
      <div ref={wrapRef} style={{ width: '100%', position: 'relative' }}>
        <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} style={{ overflow: 'visible' }}>
          {/* 横向网格 + Y 轴刻度 */}
          {yTicks.map((v, i) => {
            const y = yOf(v);
            return (
              <g key={`y-${i}`}>
                <line x1={padL} y1={y} x2={width - padR} y2={y} stroke="#f0f0f0" strokeWidth="1" />
                <text x={padL - 8} y={y + 4} textAnchor="end" fontSize="11" fill="#8c8c8c">{Math.round(v)}</text>
              </g>
            );
          })}
          {/* X 轴刻度 */}
          {xTicks.map((d, i) => (
            <text key={`x-${i}`} x={xOf(d)} y={height - 10} textAnchor="middle" fontSize="11" fill="#8c8c8c">
              {d.slice(5)}
            </text>
          ))}
          {/* 折线 */}
          {series.map((s, idx) => {
            const color = TREND_COLORS[idx % TREND_COLORS.length];
            const pts = s.points.map(p => `${xOf(p.date)},${yOf(p.value)}`).join(' ');
            return (
              <g key={s.key}>
                {s.points.length > 1 && (
                  <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                )}
                {s.points.map((p, j) => (
                  <g key={j}
                    onMouseEnter={() => setHover({ x: xOf(p.date), y: yOf(p.value), label: s.label, date: p.date, value: p.value, color })}
                    onMouseLeave={() => setHover(null)}
                    style={{ cursor: 'pointer' }}
                  >
                    {/* 透明热区，方便悬停 */}
                    <circle cx={xOf(p.date)} cy={yOf(p.value)} r="10" fill="transparent" />
                    <circle cx={xOf(p.date)} cy={yOf(p.value)} r="4" fill="#fff" stroke={color} strokeWidth="2" />
                  </g>
                ))}
              </g>
            );
          })}
        </svg>
        {hover && (
          <div style={{
            position: 'absolute',
            left: hover.x,
            top: hover.y,
            transform: 'translate(-50%, -130%)',
            background: '#fff',
            border: '1px solid #e5e5e5',
            borderRadius: 6,
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            padding: '6px 10px',
            fontSize: 12,
            lineHeight: 1.6,
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            zIndex: 10
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#595959' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: hover.color, display: 'inline-block' }} />
              {hover.label}
            </div>
            <div style={{ color: '#8c8c8c' }}>{hover.date}</div>
            <div style={{ color: '#1f1f1f', fontWeight: 600 }}>参考价格：¥{hover.value}</div>
          </div>
        )}
      </div>
      {/* 图例 */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', marginTop: 8 }}>
        {series.map((s, idx) => (
          <span key={s.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#595959' }}>
            <span style={{ width: 12, height: 3, borderRadius: 2, background: TREND_COLORS[idx % TREND_COLORS.length], display: 'inline-block' }} />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function SupplierPriceManage() {
  const { canEdit } = useMenuPermission();
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [suppliers, setSuppliers] = useState([]);
  const [growthMethods, setGrowthMethods] = useState([]);

  const [filters, setFilters] = useState({ supplier_id: undefined, growth_method_id: undefined, dateRange: null });

  // 价格趋势
  const [trendSuppliers, setTrendSuppliers] = useState([]);
  const [trendMethods, setTrendMethods] = useState([]);
  const [trendRows, setTrendRows] = useState([]);
  const [trendLoading, setTrendLoading] = useState(false);

  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  // 当前登录用户
  const user = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch (e) { return {}; }
  })();
  const isSuperAdmin = user.username === 'admin';
  const isSupplierAccount = user.role === 'supplier' && !!user.supplier_id;

  const loadOptions = async () => {
    try {
      const [supRes, gmRes] = await Promise.all([
        isSupplierAccount ? Promise.resolve({ code: 200, data: [{ id: user.supplier_id, name: user.supplier_name || '当前主体' }] }) : api.get('/suppliers/all'),
        api.get('/growth-methods')
      ]);
      if (supRes.code === 200) setSuppliers(supRes.data || []);
      if (gmRes.code === 200) setGrowthMethods(gmRes.data || []);
    } catch (e) {}
  };

  const loadData = useCallback(async (p = page, ps = pageSize, f = filters) => {
    setLoading(true);
    try {
      const params = { page: p, pageSize: ps };
      if (f.supplier_id) params.supplier_id = f.supplier_id;
      if (f.growth_method_id) params.growth_method_id = f.growth_method_id;
      if (f.dateRange && f.dateRange[0]) {
        params.start_date = f.dateRange[0].format('YYYY-MM-DD');
        params.end_date = f.dateRange[1].format('YYYY-MM-DD');
      }
      const res = await api.get('/supplier-prices', { params });
      if (res.code === 200) {
        setData(res.data.list || []);
        setTotal(res.data.total || 0);
      }
    } catch (err) {}
    setLoading(false);
  }, [page, pageSize, filters]);

  useEffect(() => { loadOptions(); }, []);
  useEffect(() => { loadData(); }, [page, pageSize, filters]);

  const loadTrend = useCallback(async () => {
    setTrendLoading(true);
    try {
      const params = {};
      if (trendSuppliers.length > 0) params.supplier_ids = trendSuppliers.join(',');
      if (trendMethods.length > 0) params.growth_method_ids = trendMethods.join(',');
      const res = await api.get('/supplier-prices/trend', { params });
      if (res.code === 200) setTrendRows(res.data || []);
    } catch (e) {}
    setTrendLoading(false);
  }, [trendSuppliers, trendMethods]);

  useEffect(() => { loadTrend(); }, [loadTrend]);

  const handleAdd = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      effective_date: dayjs(),
      supplier_ids: isSupplierAccount ? [user.supplier_id] : []
    });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditing(record);
    form.resetFields();
    form.setFieldsValue({
      growth_method_id: record.growth_method_id,
      reference_price: record.reference_price,
      effective_date: dayjs(record.effective_date),
      supplier_ids: [record.supplier_id],
      remark: record.remark
    });
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      let res;
      if (editing) {
        res = await api.put(`/supplier-prices/${editing.id}`, {
          reference_price: values.reference_price,
          effective_date: values.effective_date.format('YYYY-MM-DD'),
          remark: values.remark || ''
        });
      } else {
        res = await api.post('/supplier-prices', {
          growth_method_id: values.growth_method_id,
          reference_price: values.reference_price,
          effective_date: values.effective_date.format('YYYY-MM-DD'),
          supplier_ids: values.supplier_ids,
          remark: values.remark || ''
        });
      }
      if (res.code === 200) {
        message.success(res.message || (editing ? '修改成功' : '保存成功'));
        setModalVisible(false);
        loadData();
      }
    } catch (err) {}
  };

  const handleDelete = async (id) => {
    try {
      const res = await api.delete(`/supplier-prices/${id}`);
      if (res.code === 200) {
        message.success('删除成功');
        loadData();
      }
    } catch (err) {}
  };

  const columns = [
    {
      title: '生效日期', dataIndex: 'effective_date', width: 120,
      render: v => {
        const isFuture = dayjs(v).isAfter(dayjs(), 'day');
        return <span>{v}{isFuture && <Tag color="blue" style={{ marginLeft: 6 }}>待生效</Tag>}</span>;
      }
    },
    { title: '主体', dataIndex: 'supplier_name', width: 140 },
    { title: '涨粉方式', dataIndex: 'growth_method_name', width: 150 },
    { title: '参考价格', dataIndex: 'reference_price', width: 120, align: 'right', render: v => v != null ? `¥${v}` : '-' },
    { title: '备注', dataIndex: 'remark', ellipsis: true },
    { title: '操作人', dataIndex: 'creator_name', width: 110, render: v => v || '-' },
    { title: '创建时间', dataIndex: 'created_at', width: 170 },
    {
      title: '操作', key: 'action', width: isSuperAdmin ? 140 : 80, fixed: 'right',
      render: (_, record) => (
        <Space size={0}>
          {canEdit && <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>}
          {canEdit && isSuperAdmin && (
            <Popconfirm title="确定删除这条价格记录？" onConfirm={() => handleDelete(record.id)}>
              <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  const supplierOptions = suppliers.map(s => ({ label: s.name, value: s.id }));
  const methodOptions = growthMethods.map(g => ({ label: g.name, value: g.id }));

  return (
    <div>
      {/* 价格趋势 */}
      <Card
        size="small"
        title="参考价格趋势"
        style={{ marginBottom: 16 }}
        extra={
          <Space wrap>
            {!isSupplierAccount && (
              <Select
                mode="multiple"
                allowClear
                placeholder="选择主体（默认全部）"
                style={{ minWidth: 200 }}
                popupMatchSelectWidth={false}
                maxTagCount="responsive"
                value={trendSuppliers}
                onChange={setTrendSuppliers}
                options={supplierOptions.map(o => ({ ...o, title: o.label }))}
              />
            )}
            <Select
              mode="multiple"
              allowClear
              placeholder="选择涨粉方式（默认全部）"
              style={{ minWidth: 220 }}
              popupMatchSelectWidth={false}
              maxTagCount="responsive"
              value={trendMethods}
              onChange={setTrendMethods}
              options={methodOptions.map(o => ({ ...o, title: o.label }))}
            />
          </Space>
        }
      >
        <Spin spinning={trendLoading}>
          <PriceTrendChart rows={trendRows} />
        </Spin>
      </Card>

      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ margin: 0 }}>参考价格管理</h3>
        {canEdit && <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} size={isMobile ? 'small' : 'middle'}>新增价格调整</Button>}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        {!isSupplierAccount && (
          <Select
            allowClear
            placeholder="选择主体"
            style={{ minWidth: 160 }}
            popupMatchSelectWidth={false}
            value={filters.supplier_id}
            onChange={v => { setPage(1); setFilters(prev => ({ ...prev, supplier_id: v })); }}
            options={supplierOptions.map(o => ({ ...o, title: o.label }))}
          />
        )}
        <Select
          allowClear
          placeholder="涨粉方式"
          style={{ minWidth: 160 }}
          popupMatchSelectWidth={false}
          value={filters.growth_method_id}
          onChange={v => { setPage(1); setFilters(prev => ({ ...prev, growth_method_id: v })); }}
          options={methodOptions.map(o => ({ ...o, title: o.label }))}
        />
        <RangePicker
          value={filters.dateRange}
          onChange={v => { setPage(1); setFilters(prev => ({ ...prev, dateRange: v })); }}
        />
        <Button icon={<ReloadOutlined />} onClick={() => { setPage(1); loadData(1, pageSize, filters); }}>刷新</Button>
      </div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={loading}
        size="small"
        scroll={{ x: 1000 }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: t => `共 ${t} 条`,
          onChange: (p, ps) => { setPage(p); setPageSize(ps); }
        }}
      />

      <Modal
        title={editing ? '编辑价格记录' : '新增价格调整'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        width={520}
        okText="确认"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="growth_method_id" label="涨粉方式" rules={[{ required: true, message: '请选择涨粉方式' }]}>
            <Select
              placeholder="选择涨粉方式"
              showSearch
              optionFilterProp="label"
              disabled={!!editing}
              popupMatchSelectWidth={false}
              options={methodOptions.map(o => ({ ...o, title: o.label }))}
            />
          </Form.Item>

          <Form.Item name="reference_price" label="参考价格" rules={[{ required: true, message: '请输入参考价格' }]}>
            <InputNumber style={{ width: '100%' }} min={0} precision={2} prefix="¥" placeholder="输入参考价格" />
          </Form.Item>

          <Form.Item name="effective_date" label="生效日期" rules={[{ required: true, message: '请选择生效日期' }]}>
            <DatePicker style={{ width: '100%' }} disabled={!!editing && !isSuperAdmin ? false : false} />
          </Form.Item>

          <Form.Item name="supplier_ids" label="主体" rules={[{ required: true, message: '请选择主体' }]}>
            <Select
              mode="multiple"
              placeholder="选择主体（可多选）"
              showSearch
              optionFilterProp="label"
              disabled={isSupplierAccount || !!editing}
              popupMatchSelectWidth={false}
              maxTagCount="responsive"
              options={supplierOptions.map(o => ({ ...o, title: o.label }))}
            />
          </Form.Item>

          <div style={{ color: '#999', fontSize: 12, marginTop: -8, marginBottom: 12 }}>
            将为每个主体生成一条记录；同主体同涨粉方式同一天已有记录时会覆盖原价格。
          </div>

          <Form.Item name="remark" label="备注">
            <Input placeholder="例如：10月调价" maxLength={255} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default SupplierPriceManage;
